import React, { useEffect, useState } from "react";
import axios from "axios";
import { format } from "date-fns";
import { Map, Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogFooter, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY_FORM = { name: "", description: "", color_code: "#1B2A6B" };
const INPUT_CLS = "rounded-none border-[#E2E8F0] focus-visible:ring-0 focus-visible:border-[#1B2A6B] h-11 text-sm";

const FormField = ({ label, children }) => (
  <div className="space-y-1.5">
    <label className="text-[11px] font-bold tracking-widest uppercase text-[#1B2A6B] block">{label}</label>
    {children}
  </div>
);

export default function RegionsPage() {
  const [regions, setRegions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);

  useEffect(() => {
    axios.get(`${API}/regions`, { withCredentials: true })
      .then(({ data }) => setRegions(data))
      .catch(() => toast.error("Failed to load regions."))
      .finally(() => setLoading(false));
  }, []);

  const openAdd = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setDialogOpen(true);
  };

  const openEdit = (region) => {
    setEditing(region);
    setForm({ name: region.name, description: region.description || "", color_code: region.color_code });
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditing(null);
    setForm(EMPTY_FORM);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!form.name.trim()) return toast.error("Region name is required.");
    if (!/^#[0-9A-Fa-f]{6}$/.test(form.color_code)) return toast.error("Enter a valid 6-digit color code.");
    setSaving(true);
    try {
      const request = editing
        ? axios.patch(`${API}/regions/${editing.id}`, form, { withCredentials: true })
        : axios.post(`${API}/regions`, form, { withCredentials: true });
      const { data } = await request;
      setRegions(prev => {
        const next = editing ? prev.map(region => region.id === data.id ? data : region) : [...prev, data];
        return next.sort((a, b) => a.name.localeCompare(b.name));
      });
      toast.success(editing ? "Region updated." : "Region added successfully!");
      closeDialog();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save region.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (region) => {
    if (!window.confirm(`Delete the region “${region.name}”?`)) return;
    try {
      await axios.delete(`${API}/regions/${region.id}`, { withCredentials: true });
      setRegions(prev => prev.filter(item => item.id !== region.id));
      toast.success("Region deleted.");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete region.");
    }
  };

  return (
    <div className="space-y-5" data-testid="regions-page">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-[#1B2A6B]">Regions</h2>
          <p className="text-xs text-[#475569] mt-0.5">{regions.length} region{regions.length !== 1 ? "s" : ""} in master data</p>
        </div>
        <Button onClick={openAdd} data-testid="add-region-btn" className="rounded-none bg-[#1B2A6B] hover:bg-[#152257] text-white font-bold tracking-widest uppercase text-xs h-10 px-5 border-b-2 border-[#FFD700]">
          <Plus size={14} className="mr-1.5" />Add Region
        </Button>
      </div>

      <div className="bg-white border border-[#E2E8F0]">
        {loading ? (
          <div className="flex items-center justify-center py-20 text-sm text-[#94A3B8]">Loading...</div>
        ) : regions.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 gap-3">
            <Map size={40} className="text-[#E2E8F0]" />
            <p className="text-sm text-[#94A3B8]">No regions yet. <button onClick={openAdd} className="text-[#1B2A6B] font-semibold underline">Add your first region</button></p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="regions-table">
              <thead><tr className="bg-[#F9FAFB] border-b border-[#E2E8F0]">
                {["#", "Name", "Description", "Color Code", "Added On", ""].map(heading => <th key={heading} className="text-left px-4 py-3 text-[10px] font-bold uppercase tracking-widest text-[#475569] whitespace-nowrap">{heading}</th>)}
              </tr></thead>
              <tbody>{regions.map((region, index) => (
                <tr key={region.id} className={`border-b border-[#E2E8F0] ${index % 2 ? "bg-[#F8FAFC]" : "bg-white"}`}>
                  <td className="px-4 py-3 text-[#94A3B8] text-xs font-mono">{index + 1}</td>
                  <td className="px-4 py-3 font-semibold text-[#0F172A]">{region.name}</td>
                  <td className="px-4 py-3 text-[#475569]">{region.description || "—"}</td>
                  <td className="px-4 py-3"><span className="inline-flex items-center gap-2 font-mono text-xs"><span className="w-5 h-5 border border-black/10" style={{ backgroundColor: region.color_code }} />{region.color_code}</span></td>
                  <td className="px-4 py-3 text-xs text-[#475569] whitespace-nowrap">{format(new Date(region.created_at), "dd MMM yyyy")}</td>
                  <td className="px-4 py-3"><div className="flex gap-1">
                    <button onClick={() => openEdit(region)} className="text-[#94A3B8] hover:text-[#1B2A6B] p-1" title="Edit"><Pencil size={14} /></button>
                    <button onClick={() => handleDelete(region)} className="text-[#CBD5E1] hover:text-red-500 p-1" title="Delete"><Trash2 size={14} /></button>
                  </div></td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={open => { if (!open) closeDialog(); else setDialogOpen(true); }}>
        <DialogContent className="max-w-md rounded-none p-0 gap-0">
          <div className="bg-[#1B2A6B] px-6 py-4 border-b-4 border-[#FFD700]">
            <DialogTitle className="text-white font-bold tracking-wide text-base">{editing ? "Edit Region" : "Add New Region"}</DialogTitle>
            <p className="text-white/60 text-xs mt-0.5">Define the region used to group cities</p>
          </div>
          <form onSubmit={handleSubmit}>
            <div className="px-6 py-5 space-y-4">
              <FormField label="Name *"><Input value={form.name} onChange={e => setForm(value => ({ ...value, name: e.target.value }))} placeholder="e.g. West India" className={INPUT_CLS} autoFocus /></FormField>
              <FormField label="Description"><textarea value={form.description} onChange={e => setForm(value => ({ ...value, description: e.target.value }))} rows={3} placeholder="Optional region description..." className="w-full rounded-none border border-[#E2E8F0] focus:border-[#1B2A6B] focus:outline-none px-3 py-2.5 text-sm resize-none" /></FormField>
              <FormField label="Color Code *">
                <div className="flex gap-2">
                  <input type="color" value={/^#[0-9A-Fa-f]{6}$/.test(form.color_code) ? form.color_code : "#000000"} onChange={e => setForm(value => ({ ...value, color_code: e.target.value.toUpperCase() }))} className="w-12 h-11 border border-[#E2E8F0] bg-white p-1 cursor-pointer" />
                  <Input value={form.color_code} onChange={e => setForm(value => ({ ...value, color_code: e.target.value.toUpperCase() }))} placeholder="#1B2A6B" className={`${INPUT_CLS} font-mono`} />
                </div>
              </FormField>
            </div>
            <DialogFooter className="px-6 py-4 bg-[#F9FAFB] border-t border-[#E2E8F0] gap-2">
              <button type="button" onClick={closeDialog} className="px-5 py-2 text-xs font-bold uppercase tracking-widest text-[#475569] border border-[#E2E8F0]">Cancel</button>
              <Button type="submit" disabled={saving} className="rounded-none bg-[#1B2A6B] hover:bg-[#152257] text-white font-bold tracking-widest uppercase text-xs h-9 px-6 border-b-2 border-[#FFD700] disabled:opacity-60">{saving ? "Saving..." : editing ? "Update Region" : "Save Region"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
